import React, { ReactNode, useEffect, useRef, useState } from 'react';
import { DownloadIcon, LinkIcon, PrinterIcon, RefreshCwIcon, SaveIcon } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { MaternityFile } from '../../types';
import { DischargeSlipDocument } from './DischargeSlipDocument';
type DischargeSlipSectionProps = {
  file: MaternityFile;
  onChange: (file: MaternityFile) => void;
  onSave: () => void;
  children: ReactNode;
};
export function DischargeSlipSection({
  file,
  onChange,
  onSave,
  children
}: DischargeSlipSectionProps) {
  const [showDocument, setShowDocument] = useState(false);
  const populatedFor = useRef<string | null>(null);
  useEffect(() => {
    if (populatedFor.current === file.id) return;
    populatedFor.current = file.id;
    const populated = withRecordDefaults(file);
    if (populated !== file) onChange(populated);
  }, [file, onChange]);
  const refreshFromRecord = () => {
    onChange(withRecordDefaults(file, true));
  };
  return <section className="space-y-4" aria-labelledby="discharge-slip-heading" data-id="element-7133" data-mp="268zl3p3puu97">
      <div className="rounded-lg border border-pink-200 bg-white shadow-sm" data-id="element-7134" data-mp="21aekvpogsakr">
        <div className="flex flex-wrap items-start justify-between gap-3 border-b border-pink-100 bg-pink-50/60 px-5 py-4" data-id="element-7135" data-mp="21cim1zuzavhc">
          <div data-id="element-7136" data-mp="2b8vfqxqix9c3">
            <h2 id="discharge-slip-heading" className="text-lg font-bold text-gray-900" data-id="element-7137" data-mp="2b82egcac3c1c">
              Discharge Slip
            </h2>
            <p className="mt-1 flex items-center gap-1.5 text-sm text-gray-600" data-id="element-7138" data-mp="1ijwy01qbc97b">
              <LinkIcon className="h-3.5 w-3.5 text-pink-600" data-id="element-7139" data-mp="1ihtz0s76n5qg" />
              Linked to maternity record {file.bmcFileNumber} ·{' '}
              {file.patient?.firstName} {file.patient?.surname}
              {file.patient?.age ? `, ${file.patient.age}` : ''}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2" data-id="element-7140" data-mp="13sxq6sxaxoos">
            <Button variant="outline" size="sm" onClick={refreshFromRecord} data-id="element-7141" data-mp="zsdw5y80z16v">
              <RefreshCwIcon className="mr-1.5 h-4 w-4" data-id="element-7142" data-mp="2yyqfjwc8xuze" /> Refill from record
            </Button>
            <Button variant="outline" size="sm" onClick={() => setShowDocument(true)} data-id="element-7143" data-mp="33wrvi8edle6n">
              <PrinterIcon className="mr-1.5 h-4 w-4" data-id="element-7144" data-mp="38oiairo9cq4e" /> Print
            </Button>
            <Button variant="outline" size="sm" onClick={() => setShowDocument(true)} data-id="element-7145" data-mp="3dm1r3y8fhezi">
              <DownloadIcon className="mr-1.5 h-4 w-4" data-id="element-7146" data-mp="2g1p5vy7qz6p4" /> Download PDF
            </Button>
            <Button size="sm" onClick={onSave} className="bg-pink-600 hover:bg-pink-700 focus:ring-pink-500" data-id="element-7147" data-mp="2l0ib73cvrre7">
              <SaveIcon className="mr-1.5 h-4 w-4" data-id="element-7148" data-mp="2uxd7dflolfpb" /> Save discharge slip
            </Button>
          </div>
        </div>
        <p className="px-5 py-3 text-sm text-gray-600" data-id="element-7149" data-mp="5704mps1pt6m">
          Demographic and delivery details are carried over from the main
          maternity record. Anything you edit here is saved back to the same
          maternity episode — no separate patient file is created.
        </p>
      </div>

      {children}

      {showDocument && <DischargeSlipDocument file={file} onClose={() => setShowDocument(false)} data-id="element-7150" data-mp="1kyc5jd8zsb2f" />}
    </section>;
}
const emptySlip = {
  smNo: '',
  gravida: '',
  patientName: '',
  party: '',
  timeOfDischarge: '',
  age: '',
  motherDob: '',
  modeOfDelivery: '',
  perineum: '',
  comments: '',
  pca: '',
  infantTob: '',
  apgarScore: '',
  infantSex: '',
  dateOfDischarge: '',
  mother: {
    bp: '',
    pallor: '',
    breast: '',
    breastfeeding: '',
    uterus: '',
    perineum: '',
    lochia: '',
    bowels: '',
    urine: '',
    familyPlanning: '',
    dangerSigns: '',
    treatment: '',
    vitaminA: ''
  },
  infant: {
    birthWeight: '',
    dischargeWeight: '',
    pallor: '',
    jaundice: '',
    passedMeconiumUrine: '',
    feeding: '',
    feedingOption: '',
    breastFeeding: '',
    bcg: '',
    opv: '',
    birthRecord: '',
    gc: '',
    treatment: ''
  },
  sixDaysPostnatalDate: '',
  sixWeeksPostnatalDate: '',
  nameInFull: ''
};
function withRecordDefaults(file: MaternityFile, overwrite = false) {
  const slip = {
    ...emptySlip,
    ...(file.dischargeSlip || {}),
    mother: {
      ...emptySlip.mother,
      ...(file.dischargeSlip?.mother || {})
    },
    infant: {
      ...emptySlip.infant,
      ...(file.dischargeSlip?.infant || {})
    }
  };
  const birth = file.delivery?.deliverySummary?.[0];
  const apgar = [file.newbornEvaluation?.apgar1, file.newbornEvaluation?.apgar5, file.newbornEvaluation?.apgar10].filter(Boolean).join(' / ');
  const fill = (current: string, source?: string) => source && (overwrite || !current) ? source : current;
  const next = {
    ...slip,
    smNo: fill(slip.smNo, file.bmcFileNumber),
    patientName: fill(slip.patientName, `${file.patient?.firstName || ''} ${file.patient?.surname || ''}`.trim()),
    age: fill(slip.age, file.patient?.age),
    modeOfDelivery: fill(slip.modeOfDelivery, file.delivery?.deliveryMethod),
    perineum: fill(slip.perineum, file.delivery?.episiotomy),
    infantSex: fill(slip.infantSex, birth?.sex),
    infantTob: fill(slip.infantTob, birth?.timeOfBirth),
    apgarScore: fill(slip.apgarScore, apgar),
    infant: {
      ...slip.infant,
      birthWeight: fill(slip.infant.birthWeight, birth?.weight),
      feeding: fill(slip.infant.feeding, file.newbornEvaluation?.infantFeeding)
    }
  };
  if (JSON.stringify(next) === JSON.stringify(file.dischargeSlip)) return file;
  return {
    ...file,
    dischargeSlip: next
  };
}